import type {
    KnowledgeBaseArticle,
    KnowledgeEvent,
    KnowledgeItem,
    KnowledgeLibrary,
    KnowledgeNode,
    KnowledgeRevision,
    ListingParams,
    TeamAccess,
    RelatedArticle,
    ArticleRevision
} from '@/composables/services/types/knowledge';
import { useKnowledgeAPI } from '@/composables/services/useKnowledgeAPI';
import type { ArticleEditorOption } from '@/composables/services/useUsersAPI';
import { useMetaStore } from '@/stores/meta';
import { defineStore } from 'pinia';

// Define the interface outside of the store
interface KnowledgeListingParams {
    rootParentId: string | null;
    parentId: string | null;
}

export const useKnowledgeStore = defineStore('knowledge', {
    state: () => ({
        items: [] as KnowledgeItem[],
        treeNodes: [] as KnowledgeNode[],
        currentNode: null as KnowledgeItem | null,
        currentArticle: null as KnowledgeItem | null,
        currentRootId: null as string | null,
        loading: false,
        loadingContent: false,
        error: null as string | null,
        selectedCategory: '',
        showFavorites: false,
        libraries: [] as KnowledgeLibrary[],
        loadingLibraries: false,
        selectedLibrary: null as KnowledgeLibrary | null,
        searchQuery: '',
        currentList: [] as KnowledgeItem[],
        allFilteredResults: [] as KnowledgeItem[], // Store all filtered results for client-side pagination
        totalCount: 0,
        isClientSidePagination: false, // Flag to indicate if we're using client-side pagination
        selectedNodeKey: {} as Record<string, boolean>,
        selectedLibraryId: null as string | null,
        selectedLabelId: null as string | null,
        sidebarSelectionChanged: false,
        // Filter state - persisted across navigation
        statusFilter: null as string | null,
        accessFilter: null as string | null,
        productsFilter: [] as string[],
        tagsFilter: [] as string[],
        // Session-scoped cache for article editor users (cleared on page refresh only)
        articleEditors: [] as ArticleEditorOption[],
        teams: [] as TeamAccess[],
        loadingTeams: false,
        availableKnowledgeBases: [] as KnowledgeBaseArticle[],
        loadingKnowledgeBases: false,
        labelTree: [] as KnowledgeNode[],
        labelMap: {} as Record<string, string>,
        selectedContentType: 'articles',
        comments: [] as KnowledgeEvent[],
        loadingComments: false,
        commentsError: null as string | null,
        totalCommentsCount: 0,
        // Expanded state persistence for sidebar tree
        expandedKeys: {} as Record<string, boolean>,
        // Cache for tree data to avoid refetching
        treeDataCache: null as KnowledgeNode[] | null,
        treeDataCacheTimestamp: null as number | null,
        
        // Article view state management
        currentSelectedRevision: null as any | null,
        currentArticleRevisions: [] as ArticleRevision[],
        loadingRevisions: false,
        relatedArticles: [] as RelatedArticle[],
        loadingRelatedArticles: false,
        
        // UI state management
        isPanelMinimized: false,
        isEditing: false,
        
        // Article library CSS cache
        articleLibraryCssCache: {} as Record<string, string>,
    }),

    getters: {
        getItemById: (state) => {
            return (id: string) => state.items.find((item) => item.id === id);
        },
        getItemsByTag: (state) => {
            return (tag: string) =>
                state.items.filter((item) => item.metadata?.tags && item.metadata.tags.includes(tag));
        },
        filteredItems: (state) => {
            // If items array is empty, just return it
            if (!state.items || state.items.length === 0) {
                return [];
            }

            // Check if there are any filters active
            const hasFilters =
                state.selectedCategory || state.searchQuery || (state.statusFilter && state.statusFilter !== 'all') || state.showFavorites;

            // If no filters, return all items
            if (!hasFilters) {
                return state.items;
            }

            // Otherwise apply filters
            const filtered = state.items.filter((item) => {
                // Skip items without required properties for filtering
                if (!item) return false;

                // Category filter
                const matchesCategory = !state.selectedCategory || item.metadata?.category === state.selectedCategory;

                // Search filter - make case insensitive
                const searchLower = state.searchQuery.toLowerCase();
                const matchesSearch = !searchLower || (item.title && item.title.toLowerCase().includes(searchLower));

                // Status filter
                const matchesStatus = !state.statusFilter || state.statusFilter === 'all' || item.status === state.statusFilter;

                // Favorites filter (not implemented yet, just a placeholder)
                const matchesFavorites =
                    !state.showFavorites || (item.metadata?.tags && item.metadata.tags.includes('favorite'));

                return matchesCategory && matchesSearch && matchesStatus && matchesFavorites;
            });

            return filtered;
        },
    },

    actions: {
        async fetchKnowledgeTree(forceRefresh: boolean = false) {
            // Check if we have cached data and it's still fresh (less than 5 minutes old)
            const cacheMaxAge = 5 * 60 * 1000; // 5 minutes in milliseconds
            const now = Date.now();
            
            if (!forceRefresh && 
                this.treeDataCache && 
                this.treeDataCacheTimestamp && 
                (now - this.treeDataCacheTimestamp) < cacheMaxAge) {
                // Use cached data
                this.treeNodes = this.treeDataCache;
                return this.treeNodes;
            }

            this.loading = true;
            this.error = null;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                
                // Fetch the main tree (excluding Shared with Me library)
                const freshData = await knowledgeAPI.fetchKnowledgeTree();
                
                // Check if Shared with Me library should be included
                const SHARED_WITH_ME_ID = '00000001-0001-0001-0001-000000000001';
                const sharedWithMeCount = await knowledgeAPI.checkSharedWithMeArticleCount();
                
                // Check if Shared with Me library already exists in the tree
                const sharedWithMeExists = freshData.some(node => node.id === SHARED_WITH_ME_ID);
                
                let finalTreeData = [...freshData];
                
                if (sharedWithMeCount > 0) {
                    if (sharedWithMeExists) {
                        // Shared with Me library already exists, no need to update it
                        finalTreeData = freshData;
                    } else {
                        // Add Shared with Me library to the bottom of the tree
                        const sharedWithMeNode = await knowledgeAPI.fetchSharedWithMeLibraryNode();
                        if (sharedWithMeNode) {
                            // Don't set article_cnt to keep it consistent with other libraries
                            finalTreeData = [...freshData, sharedWithMeNode];
                        }
                    }
                } else if (sharedWithMeExists) {
                    // Remove Shared with Me library if it has no articles
                    finalTreeData = freshData.filter(node => node.id !== SHARED_WITH_ME_ID);
                }
                
                // Update both the current tree and the cache
                this.treeNodes = finalTreeData;
                this.treeDataCache = JSON.parse(JSON.stringify(finalTreeData)); // Deep copy
                this.treeDataCacheTimestamp = now;
                
                return this.treeNodes;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to fetch knowledge tree';
                console.error('Error fetching knowledge tree:', err);
                // Don't throw here, let the component handle the error display
                return [];
            } finally {
                this.loading = false;
            }
        },

        // Fetch tree nodes without affecting other state like current list or loading state
        async fetchKnowledgeTreeOnly() {
            try {
                const knowledgeAPI = useKnowledgeAPI();
                
                // Fetch the main tree (excluding Shared with Me library)
                const freshData = await knowledgeAPI.fetchKnowledgeTree();
                
                // Check if Shared with Me library should be included
                const SHARED_WITH_ME_ID = '00000001-0001-0001-0001-000000000001';
                const sharedWithMeCount = await knowledgeAPI.checkSharedWithMeArticleCount();
                
                // Check if Shared with Me library already exists in the tree
                const sharedWithMeExists = freshData.some(node => node.id === SHARED_WITH_ME_ID);
                
                let finalTreeData = [...freshData];
                
                if (sharedWithMeCount > 0) {
                    if (sharedWithMeExists) {
                        // Shared with Me library already exists, no need to update it
                        finalTreeData = freshData;
                    } else {
                        // Add Shared with Me library to the bottom of the tree
                        const sharedWithMeNode = await knowledgeAPI.fetchSharedWithMeLibraryNode();
                        if (sharedWithMeNode) {
                            // Don't set article_cnt to keep it consistent with other libraries
                            finalTreeData = [...freshData, sharedWithMeNode];
                        }
                    }
                } else if (sharedWithMeExists) {
                    // Remove Shared with Me library if it has no articles
                    finalTreeData = freshData.filter(node => node.id !== SHARED_WITH_ME_ID);
                }
                
                this.treeNodes = finalTreeData;
                return this.treeNodes;
            } catch (err) {
                console.error('Error fetching knowledge tree:', err);
                // Don't throw here, just return empty array
                return [];
            }
        },

        async fetchChildNodes(nodeId: string) {
            this.loading = true;
            this.error = null;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const children = await knowledgeAPI.fetchKnowledgeTree({ node: nodeId });
                // Find the node with this ID and update its children
                this.updateNodeChildren(this.treeNodes, nodeId, children);
                return children;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to fetch child nodes';
                console.error('Error fetching child nodes:', err);
                // Still throw the error so component can handle it with toast
                throw err;
            } finally {
                this.loading = false;
            }
        },

        // Helper function to recursively update children of a node
        updateNodeChildren(nodes: KnowledgeNode[], nodeId: string, children: KnowledgeNode[], updateCache: boolean = true) {
            for (const node of nodes) {
                if (node.id === nodeId) {
                    node.children = children;
                    
                    // Also update the cache if it exists (but prevent recursive calls)
                    if (updateCache && this.treeDataCache) {
                        this.updateNodeChildren(this.treeDataCache, nodeId, children, false);
                    }
                    
                    return true;
                }
                if (node.children && this.updateNodeChildren(node.children, nodeId, children, updateCache)) {
                    return true;
                }
            }
            return false;
        },

        async fetchKnowledgeItem(id: string) {
            this.loading = true;
            this.error = null;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.fetchKnowledgeItem(id);
                this.currentNode = response.data || null;
                return response;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to fetch knowledge item';
                console.error('Error fetching knowledge item:', err);
                throw err;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Store tags from article data in the meta store
         * Creates mini tag objects from bc__tags_support (IDs) and c__support_labels (labels) arrays
         * and adds them directly to pl__tags in metaData
         */
        storeTagsFromArticle(article: KnowledgeItem) {
            // bc__tags_support contains tag IDs, c__support_labels contains corresponding labels
            // Convert to array if it's an array-like object
            let tagIds = (article as any).bc__tags_support;
            let tagLabels = (article as any).c__support_labels;
            
            // Handle array-like objects (objects with numeric keys)
            if (tagIds && typeof tagIds === 'object' && !Array.isArray(tagIds)) {
                tagIds = Object.values(tagIds);
            }
            
            if (tagLabels && typeof tagLabels === 'object' && !Array.isArray(tagLabels)) {
                tagLabels = Object.values(tagLabels);
            }
            
            if (!tagIds || !Array.isArray(tagIds) || tagIds.length === 0) {
                return;
            }
            
            try {
                const metaStore = useMetaStore();
                
                // Ensure metaData and pl__tags exist
                if (!metaStore.metaData) {
                    return;
                }
                
                if (!metaStore.metaData.pl__tags) {
                    metaStore.metaData.pl__tags = [];
                }
                
                // Get existing tag IDs from pl__tags
                const existingTagIds = new Set(
                    metaStore.metaData.pl__tags.map((tag: any) => tag.id || tag.val)
                );
                
                // Create mini tag objects for tags that don't exist in pl__tags
                const newTags: any[] = [];
                
                tagIds.forEach((id: any, index: number) => {
                    // Skip if this tag already exists in pl__tags
                    if (existingTagIds.has(id)) {
                        return;
                    }
                    
                    // Get the corresponding label (if arrays are aligned)
                    const label = tagLabels && Array.isArray(tagLabels) && tagLabels[index] 
                        ? String(tagLabels[index]).trim()  // Trim whitespace
                        : undefined;
                    
                    // Create mini tag object matching pl__tags structure
                    const miniTag = {
                        id: id,
                        val: id,
                        lbl: label,
                        category: 3, // Support tags
                        c__partners_teams_ids: [], // No team restrictions for article tags by default
                        category_tag_ids: [] // No category restrictions
                    };
                    
                    newTags.push(miniTag);
                });
                
                // Add new tags directly to pl__tags
                if (newTags.length > 0) {
                    metaStore.metaData.pl__tags.push(...newTags);
                }
            } catch (err) {
                console.error('Error storing tags from article:', err);
                // Don't throw - tags are supplementary data, shouldn't fail the whole load
            }
        },

        async fetchKnowledgeListing(params: KnowledgeListingParams, additionalParams?: ListingParams) {
            this.loadingContent = true;
            this.error = null;
            this.currentRootId = params.rootParentId;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                let apiParams: ListingParams;
                if (additionalParams) {
                    apiParams = { ...additionalParams };
                } else {
                    // Build the filter array, only including root_parent_id if present
                    const filter = [

                        { property: 'type', value: 0 },
                        { property: 'id', value: '_no_filter_' },
                        { property: 'parent_id', value: params.parentId },
                        { property: 'partner_ids', operator: 'intersect_set', value: null },
                        { property: 'visibility', operator: '=', value: null },
                        { property: 'status', operator: '=', value: null },
                        { property: 'owner_partner_id', value: '_no_filter_' },
                        { property: 'dict_id', value: null },
                        { property: 'tag_ids', value: null },
                    ];
                    if (params.rootParentId !== null && params.rootParentId !== undefined) {
                        filter.splice(3, 0, { property: 'root_parent_id', value: params.rootParentId });
                    }
                    apiParams = {
                        page: 1,
                        start: 0,
                        limit: 50,
                        filter,
                    };
                }

                // If we're using default params, make sure parent_id and root_parent_id are set correctly
                if (!additionalParams && apiParams.filter) {
                    // Find and update the parent_id filter
                    const parentIdIndex = apiParams.filter.findIndex((f) => f.property === 'parent_id');
                    if (parentIdIndex >= 0) {
                        apiParams.filter[parentIdIndex].value = params.parentId;
                    }
                }

                const response = await knowledgeAPI.fetchKnowledgeListing(apiParams);
                
                // Store tags from each article into meta store
                response.items.forEach(article => {
                    this.storeTagsFromArticle(article);
                });
                
                // Check if this is a search with high limit (all results returned)
                // When searching with a query, we fetch all results and paginate client-side
                const isSearchWithAllResults = apiParams.limit && apiParams.limit >= 10000;
                
                if (isSearchWithAllResults) {
                    // Store all search results for client-side pagination
                    // This allows the user to paginate through all search results
                    // while respecting their rowsPerPage preference
                    this.allFilteredResults = response.items.map((item) => ({ ...item }));
                    this.isClientSidePagination = true;
                    this.totalCount = response.items.length; // Use actual result count
                    this.currentList = response.items.map((item) => ({ ...item }));
                } else {
                    // Normal server-side pagination
                    this.allFilteredResults = [];
                    this.isClientSidePagination = false;
                    this.currentList = response.items.map((item) => ({ ...item }));
                    this.totalCount = response.totalCount;
                }
                
                // Update items array
                this.items = response.items.map((item) => ({ ...item }));

                return response.items;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to fetch knowledge listing';
                console.error('Error fetching knowledge listing:', err);
                throw err;
            } finally {
                this.loadingContent = false;
            }
        },

        addItem(item: KnowledgeItem) {
            this.items.push(item);
        },

        async updateRevisionToDraft(kbId: string, updates: Partial<KnowledgeItem>, revisionId: string) {
            const index = this.items.findIndex((item) => item.id === kbId);
            if (index !== -1) {
                this.items[index].status = 'DRAFT';
            }
            // if (updates.content) {
            await this.saveItemRevision(kbId, updates.content ?? '', revisionId, true);
            // }
        },

        async publishRevision(kbId: string, updates: Partial<KnowledgeItem>, revisionId: string) {
            const index = this.items.findIndex((item) => item.id === kbId);
            if (index !== -1) {
                this.items[index].status = 'PUBLISHED';
            }
            //            if (updates.content) {
            await this.saveItemRevision(kbId, updates.content ?? '', revisionId, false, true);
            //           }
        },

        async updateItem(kbId: string, updates: Partial<KnowledgeItem>, revisionId: string, force: boolean = false) {
            const index = this.items.findIndex((item) => item.id === kbId);
            if (index !== -1 || force) {
                this.items[index] = {
                    ...this.items[index],
                    ...updates,
                };

                // If content is being updated, save it as a revision
                if (updates.content) {
                    // Save the content, title, and subtitle to the revision only
                    return await this.saveItemRevision(
                        kbId,
                        updates.content,
                        revisionId,
                        false,
                        false,
                        updates.title,
                        updates.sub_title
                    );
                }
            }
        },

        async saveItemRevision(
            kbId: string,
            body: string,
            revisionId: string,
            draft: boolean = false,
            publish: boolean = false,
            title?: string,
            sub_title?: string
        ) {
            try {
                const revision: KnowledgeRevision & { title?: string; sub_title?: string } = {
                    id: revisionId,
                    body: '<bt/>'+ body,
                };
                if (title) {
                    revision.title = title;
                }
                if (sub_title) {
                    revision.sub_title = sub_title;
                }
                if (draft) {
                    revision['_copy_revision'] = true;
                }
                if (publish) {
                    revision['_publish'] = true;
                }

                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.putRevisions([revision]);
                return response;
            } catch (err) {
                console.error('Error saving revision:', err);
                this.error = err instanceof Error ? err.message : 'Failed to save revision';
                // We don't throw the error here to prevent blocking the UI update
                // The error is logged and stored in the error state
            }
        },

        setSearchQuery(query: string) {
            this.searchQuery = query;
            if (typeof localStorage !== 'undefined') {
                if (query) {
                    localStorage.setItem('knowledge-search-query', query);
                } else {
                    localStorage.removeItem('knowledge-search-query');
                }
            }
        },

        deleteItem(id: string) {
            const index = this.items.findIndex((item) => item.id === id);
            if (index !== -1) {
                this.items.splice(index, 1);
            }
        },

        async fetchArticleById(id: string) {
            this.loading = true;
            this.error = null;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const apiParams: ListingParams = {
                    page: 1,
                    start: 0,
                    limit: 1,
                    filter: [{ property: 'id', value: id }],
                };

                const response = await knowledgeAPI.fetchKnowledgeListing(apiParams);

                const article = response.items.find((article: KnowledgeItem) => article.id === id);
                if (article) {
                    // Store tags from this article into meta store
                    this.storeTagsFromArticle(article);
                    
                    this.currentArticle = article;
                    
                    // Update the article in currentList and items arrays if it exists
                    const currentListIndex = this.currentList.findIndex(item => item.id === id);
                    if (currentListIndex !== -1) {
                        this.currentList[currentListIndex] = { ...article };
                    }
                    
                    const itemsIndex = this.items.findIndex(item => item.id === id);
                    if (itemsIndex !== -1) {
                        this.items[itemsIndex] = { ...article };
                    }

                    return this.currentArticle;
                } else {
                    this.error = `Article with ID ${id} not found`;
                    throw new Error(this.error);
                }
            } catch (err) {
                this.error = err instanceof Error ? err.message : `Failed to fetch article with ID ${id}`;
                console.error('Error fetching article by ID:', err);
                throw err;
            } finally {
                this.loading = false;
            }
        },

        clearCurrentArticle() {
            this.currentArticle = null;
        },

        clearCurrentList() {
            this.currentList = [];
            this.currentRootId = null;
        },

        async fetchLibraries() {
            this.loadingLibraries = true;
            this.error = null;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const libraries = await knowledgeAPI.fetchKnowledgeLibraries();
                this.libraries = libraries;

                // If we have libraries and no selected library, select the first one
                if (libraries.length > 0 && !this.selectedLibrary) {
                    this.selectedLibrary = libraries[0];
                }

                return libraries;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to fetch knowledge libraries';
                console.error('Error fetching knowledge libraries:', err);
                throw err;
            } finally {
                this.loadingLibraries = false;
            }
        },

        // Load libraries without any side effects on the current list
        async fetchLibrariesOnly() {
            this.loadingLibraries = true;
            this.error = null;

            try {
                // Don't set the error state to avoid triggering watchers
                const knowledgeAPI = useKnowledgeAPI();
                const libraries = await knowledgeAPI.fetchKnowledgeLibraries();
                this.libraries = libraries;

                // If we have libraries and no selected library, select the first one
                if (libraries.length > 0 && !this.selectedLibrary) {
                    this.selectedLibrary = libraries[0];
                }

                return libraries;
            } catch (err) {
                console.error('Error fetching knowledge libraries:', err);
                // Return an empty array instead of throwing
                return [];
            } finally {
                this.loadingLibraries = false;
            }
        },

        setSelectedLibrary(library: KnowledgeLibrary) {
            this.selectedLibrary = library;
        },

        setCurrentArticle(article: KnowledgeItem) {
            this.currentArticle = article;
        },

        async createArticle(title: string, libraryId: string) {
            this.loading = true;
            this.error = null;

            try {
                // If using the default fallback library ID, try to use the first real library or a hardcoded one
                let targetLibraryId = libraryId;
                if (libraryId === 'default') {
                    // If we have libraries in the store, use the first one
                    if (this.libraries && this.libraries.length > 0) {
                        targetLibraryId = this.libraries[0].id;

                    } else {
                        // Otherwise use the current root ID if available (meaning we're in a library)
                        if (this.currentRootId) {
                            targetLibraryId = this.currentRootId;

                        } else {
                            console.warn('No library ID available, article creation might fail');
                            // We'll try with the provided default ID anyway
                        }
                    }
                }

                // Create the article data payload
                const articleData = {
                    title: title,
                    root_parent_id: targetLibraryId,
                    short_name: title.toLowerCase().replace(/\s+/g, '-'),
                    id: 'boomtown.kb.model.Article-1', // This will be replaced by server
                    owner_partner_id: '',
                    partner_ids: [],
                    internal_team_ids: [],
                    sub_title: '',
                    body: '',
                    saved_reply: '',
                    visibility: 1,
                    searchable: true,
                    status: 0, // Published
                    type: 0,
                    updated: '',
                    created: '',
                    _merge: '',
                    merge_ids: '',
                    url: '',
                    url_avatar: '',
                    bc__tags_object_kb: [],
                    bc__tags_positions_object_kb: [],
                    c__contributor_user_ids: [],
                    _latestDraftId: '',
                    auto_gen_articles: false,
                    bc__tags_object_kb_labels: [],
                    c__org_names: [],
                    bc__tags_object_members_devices_dict: [],
                    c__tech_labels: [],
                    bc__tags_support: [],
                    c__support_labels: [],
                    editor_user_ids: [],
                    c__editor_user_names: [],
                    keywords: '',
                    url_login: '',
                    _canWrite: true,
                    _uiAccess: {
                        edit: true,
                        merge: true,
                        clone: true,
                        delete: true,
                    },
                };

                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.createKnowledgeArticle(articleData);

                // Refresh the current list to show the new article
                await this.fetchKnowledgeListing({
                    rootParentId: this.currentRootId,
                    parentId: null,
                });

                return response;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to create article';
                console.error('Error creating article:', err);
                throw err;
            } finally {
                this.loading = false;
            }
        },

        async cloneArticle(articleId: string) {
            this.loading = true;
            this.error = null;

            try {
                // First, fetch the article to be cloned
                const sourceArticle = await this.fetchArticleById(articleId);

                if (!sourceArticle) {
                    throw new Error(`Could not find article with ID ${articleId} to clone`);
                }



                // Create a base article data structure
                const clonedArticleData: Record<string, any> = {
                    title: `${sourceArticle.title} (Copy)`,
                    short_name: `${sourceArticle.title} (Copy)`.toLowerCase().replace(/\s+/g, '-'),
                    id: 'boomtown.kb.model.Article-1', // This will be replaced by server
                    sub_title: '',
                    body: sourceArticle.body || sourceArticle.content || '',
                    saved_reply: '',
                    visibility: 1,
                    searchable: true,
                    status: 1, // Set as draft initially
                    type: 0,
                    updated: '',
                    created: '',
                    _merge: '',
                    merge_ids: '',
                    url: '',
                    url_avatar: '',
                    bc__tags_object_kb: [],
                    bc__tags_linked_kb: [], // Required for linked KB relationships
                    bc__tags_positions_object_kb: [],
                    c__contributor_user_ids: [],
                    _latestDraftId: '',
                    auto_gen_articles: false,
                    bc__tags_object_kb_labels: [],
                    c__org_names: [],
                    bc__tags_object_members_devices_dict: [],
                    c__tech_labels: [],
                    bc__tags_support: [],
                    c__support_labels: [],
                    editor_user_ids: [],
                    c__editor_user_names: [],
                    keywords: '',
                    url_login: '',
                    _canWrite: true,
                    _uiAccess: {
                        edit: true,
                        merge: true,
                        clone: true,
                        delete: true,
                    },
                    _clone: true, // Mark this as a clone operation
                    _source: articleId, // Reference to the source article being cloned
                };

                // Copy properties from source article if they exist
                const propertiesToCopy = [
                    'root_parent_id',
                    'owner_partner_id',
                    'partner_ids',
                    'internal_team_ids',
                    'sub_title',
                    'visibility',
                    'searchable',
                    'url',
                    'url_avatar',
                    'bc__tags_object_kb',
                    'bc__tags_linked_kb',
                    'bc__tags_positions_object_kb',
                    'c__contributor_user_ids',
                    'auto_gen_articles',
                    'bc__tags_object_kb_labels',
                    'c__org_names',
                    'bc__tags_object_members_devices_dict',
                    'c__tech_labels',
                    'bc__tags_support',
                    'c__support_labels',
                    'editor_user_ids',
                    'c__editor_user_names',
                    'keywords',
                    'url_login',
                ];

                // Copy properties only if they exist in the source article
                for (const prop of propertiesToCopy) {
                    if (prop in sourceArticle) {
                        clonedArticleData[prop] = sourceArticle[prop as keyof typeof sourceArticle];
                    }
                }

                // Special handling for metadata if it exists
                if (sourceArticle.metadata) {
                    for (const key in sourceArticle.metadata) {
                        const metaKey = `c__${key}`;
                        // Use a type assertion to avoid the index signature error
                        clonedArticleData[metaKey] = (sourceArticle.metadata as Record<string, any>)[key];
                    }
                }



                // Create the cloned article
                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.createKnowledgeArticle(clonedArticleData);

                // Refresh the current list to show the new article
                await this.fetchKnowledgeListing({
                    rootParentId: this.currentRootId,
                    parentId: null,
                });

                return response;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to clone article';
                console.error('Error cloning article:', err);
                throw err;
            } finally {
                this.loading = false;
            }
        },

        async archiveArticle(articleId: string) {
            this.loading = true;
            this.error = null;

            try {


                // Call the API to archive the article
                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.archiveArticle(articleId);

                // Refresh the current list to reflect changes
                await this.fetchKnowledgeListing({
                    rootParentId: this.currentRootId,
                    parentId: null,
                });

                return response;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to archive article';
                console.error('Error archiving article:', err);
                throw err;
            } finally {
                this.loading = false;
            }
        },

        async deleteDraft(itemId: string) {
            this.loading = true;
            this.error = null;

            try {


                // Call the API to delete the draft revision
                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.deleteRevision([{ id: itemId, status: 99 }]);

                // Refresh the current list to reflect changes
                await this.fetchKnowledgeListing({
                    rootParentId: this.currentRootId,
                    parentId: null,
                });

                return response;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to delete draft';
                console.error('Error deleting draft:', err);
                throw err;
            } finally {
                this.loading = false;
            }
        },

        async deleteArticle(articleId: string) {
            this.loading = true;
            this.error = null;

            try {


                // Call the API to delete the article
                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.deleteArticle(articleId);

                // Refresh the current list to reflect changes
                await this.fetchKnowledgeListing({
                    rootParentId: this.currentRootId,
                    parentId: null,
                });

                return response;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to delete article';
                console.error('Error deleting article:', err);
                throw err;
            } finally {
                this.loading = false;
            }
        },

        async fetchTeams() {
            this.loadingTeams = true;
            this.error = null;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const teams = await knowledgeAPI.fetchTeams();
                this.teams = teams;
                return teams;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to fetch teams';
                console.error('Error fetching teams:', err);
                throw err;
            } finally {
                this.loadingTeams = false;
            }
        },

        // Load teams without any side effects
        async fetchTeamsOnly() {
            this.loadingTeams = true;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const teams = await knowledgeAPI.fetchTeams();
                this.teams = teams;
                return teams;
            } catch (err) {
                console.error('Error fetching teams:', err);
                // Return an empty array instead of throwing
                return [];
            } finally {
                this.loadingTeams = false;
            }
        },

        async fetchAvailableKnowledgeBases() {
            this.loadingKnowledgeBases = true;
            this.error = null;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const kbs = await knowledgeAPI.fetchAvailableKnowledgeBases();
                this.availableKnowledgeBases = kbs;
                return kbs;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to fetch available knowledge bases';
                console.error('Error fetching available knowledge bases:', err);
                throw err;
            } finally {
                this.loadingKnowledgeBases = false;
            }
        },

        // Add a method to fetch without affecting error state
        async fetchAvailableKnowledgeBasesOnly() {
            this.loadingKnowledgeBases = true;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const kbs = await knowledgeAPI.fetchAvailableKnowledgeBases();
                this.availableKnowledgeBases = kbs;
                return kbs;
            } catch (err) {
                console.error('Error fetching available knowledge bases:', err);
                return [];
            } finally {
                this.loadingKnowledgeBases = false;
            }
        },

        async fetchLabelTree() {
            try {
                // Fetch the root label tree (like in KnowledgeSidePanel)
                const knowledgeAPI = useKnowledgeAPI();
                const tree = await knowledgeAPI.fetchKnowledgeTree();
                this.labelTree = tree;

                // Flatten ALL nodes (libraries and their children) into a map of id -> name
                const map: Record<string, string> = {};

                function flattenAllNodes(nodes: any[]) {
                    for (const node of nodes) {
                        // For libraries (top-level), use the text property
                        if (node.isLibrary) {
                            const libraryName = node.text || node.title;
                            if (libraryName) {
                                map[node.id] = libraryName;
                            }
                        } else {
                            // For labels (children), use title if available, otherwise fall back to text
                            const labelName = node.title || node.text;
                            if (labelName) {
                                map[node.id] = labelName;
                            }
                        }

                        // Recursively process children
                        if (node.children && node.children.length > 0) {
                            flattenAllNodes(node.children);
                        }
                    }
                }

                flattenAllNodes(tree);
                this.labelMap = map;
                return tree;
            } catch (err) {
                console.error('Error fetching label tree:', err);
                this.labelTree = [];
                this.labelMap = {};
                return [];
            }
        },

        async updateArticleField(revisionId: string, field: string, value: any) {
            try {
                const knowledgeAPI = useKnowledgeAPI();
                const revision: KnowledgeRevision & Record<string, any> = { id: revisionId };
                revision[field] = value;
                const response = await knowledgeAPI.putRevisions([revision]);
                return response;
            } catch (error) {
                console.error('Error updating article field:', error);
                throw error;
            }
        },

        setSelectedContentType(type: string) {
            this.selectedContentType = type;
        },

        async fetchComments(params: ListingParams = {}) {
            this.loadingComments = true;
            this.commentsError = null;

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.fetchKnowledgeComments(params);

                // Update comments and total count
                if (response.data?.items) {
                    this.comments = response.data.items;
                    this.totalCommentsCount = response.data.totalCount;
                } else if (response.kb_log?.results) {
                    this.comments = response.kb_log.results;
                    this.totalCommentsCount = response.kb_log.totalCount;
                }

                return this.comments;
            } catch (err) {
                this.commentsError = err instanceof Error ? err.message : 'Failed to fetch comments';
                console.error('Error fetching comments:', err);
                throw err;
            } finally {
                this.loadingComments = false;
            }
        },

        async resolveComment(commentId: string) {
            try {
                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.resolveComment(commentId);

                // Remove the resolved comment from the list
                this.comments = this.comments.filter((comment) => comment.id !== commentId);
                this.totalCommentsCount--;

                return response;
            } catch (err) {
                console.error('Error resolving comment:', err);
                throw err;
            }
        },

        async updateArticleLibrary(article: any, rootParentId: string) {
            try {
                const knowledgeAPI = useKnowledgeAPI();
                return await knowledgeAPI.updateArticleLibrary(article, rootParentId);
            } catch (error) {
                console.error('Error updating article library:', error);
                throw error;
            }
        },

        // Method to update individual labels in the labelMap
        updateLabelMap(labels: Record<string, string>) {
            // Use Object.assign to trigger reactivity properly
            Object.assign(this.labelMap, labels);
        },

        // Method to add a single label to the labelMap
        addLabelToMap(id: string, name: string) {
            this.labelMap[id] = name;
        },

        // Methods to update selected library and label for filter integration
        setSelectedLibraryId(libraryId: string | null) {
            this.selectedLibraryId = libraryId;
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                if (libraryId) {
                    localStorage.setItem('knowledge-library-filter', libraryId);
                } else {
                    localStorage.removeItem('knowledge-library-filter');
                }
            }
        },

        setSelectedLabelId(labelId: string | null) {
            this.selectedLabelId = labelId;
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                if (labelId) {
                    localStorage.setItem('knowledge-label-filter', labelId);
                } else {
                    localStorage.removeItem('knowledge-label-filter');
                }
            }
        },

        // Clear both library and label selections
        clearLibraryAndLabelSelection() {
            this.selectedLibraryId = null;
            this.selectedLabelId = null;
            this.selectedNodeKey = {};
            // Clear from localStorage
            if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('knowledge-selected-node');
                localStorage.removeItem('knowledge-library-filter');
                localStorage.removeItem('knowledge-label-filter');
            }
        },

        // Methods to update filter state
        setStatusFilter(status: string | null) {
            this.statusFilter = status;
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                if (status) {
                    localStorage.setItem('knowledge-status-filter', status);
                } else {
                    localStorage.removeItem('knowledge-status-filter');
                }
            }
        },

        setAccessFilter(access: string | null) {
            this.accessFilter = access;
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                if (access) {
                    localStorage.setItem('knowledge-access-filter', access);
                } else {
                    localStorage.removeItem('knowledge-access-filter');
                }
            }
        },

        setProductsFilter(products: string[]) {
            this.productsFilter = products;
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                if (products.length > 0) {
                    localStorage.setItem('knowledge-products-filter', JSON.stringify(products));
                } else {
                    localStorage.removeItem('knowledge-products-filter');
                }
            }
        },

        setTagsFilter(tags: string[]) {
            this.tagsFilter = tags;
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                if (tags.length > 0) {
                    localStorage.setItem('knowledge-tags-filter', JSON.stringify(tags));
                } else {
                    localStorage.removeItem('knowledge-tags-filter');
                }
            }
        },

        setArticleEditors(editors: ArticleEditorOption[]) {
            this.articleEditors = editors;
        },

        // Clear all filters
        clearAllFilters() {
            this.statusFilter = null;
            this.accessFilter = null;
            this.productsFilter = [];
            this.tagsFilter = [];
            this.searchQuery = '';
            this.clearLibraryAndLabelSelection();
            // Clear from localStorage
            if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('knowledge-status-filter');
                localStorage.removeItem('knowledge-access-filter');
                localStorage.removeItem('knowledge-products-filter');
                localStorage.removeItem('knowledge-tags-filter');
                localStorage.removeItem('knowledge-search-query');
            }
        },

        // Restore filters from localStorage
        restoreFiltersFromLocalStorage() {
            if (typeof localStorage !== 'undefined') {
                const library = localStorage.getItem('knowledge-library-filter');
                if (library) {
                    this.selectedLibraryId = library;
                }

                const label = localStorage.getItem('knowledge-label-filter');
                if (label) {
                    this.selectedLabelId = label;
                    // If we have a label, set the selectedNodeKey to the label (label takes precedence)
                    this.selectedNodeKey = { [label]: true };
                } else if (library) {
                    // If we only have a library, set the selectedNodeKey to the library
                    this.selectedNodeKey = { [library]: true };
                }

                const status = localStorage.getItem('knowledge-status-filter');
                if (status) this.statusFilter = status;

                const access = localStorage.getItem('knowledge-access-filter');
                if (access) this.accessFilter = access;

                const products = localStorage.getItem('knowledge-products-filter');
                if (products) {
                    try {
                        this.productsFilter = JSON.parse(products);
                    } catch (e) {
                        this.productsFilter = [];
                    }
                }

                const tags = localStorage.getItem('knowledge-tags-filter');
                if (tags) {
                    try {
                        this.tagsFilter = JSON.parse(tags);
                    } catch (e) {
                        this.tagsFilter = [];
                    }
                }

                const searchQuery = localStorage.getItem('knowledge-search-query');
                if (searchQuery) this.searchQuery = searchQuery;
            }
        },

        // Set selected node key with persistence
        setSelectedNodeKey(nodeKey: Record<string, boolean>) {
            this.selectedNodeKey = nodeKey;
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('knowledge-selected-node', JSON.stringify(nodeKey));
            }
        },

        // Get selected node key with persistence
        getSelectedNodeKey(): Record<string, boolean> {
            // If we have state in memory, use it
            if (Object.keys(this.selectedNodeKey).length > 0) {
                return { ...this.selectedNodeKey };
            }
            
            // Otherwise, try to load from localStorage
            if (typeof localStorage !== 'undefined') {
                const stored = localStorage.getItem('knowledge-selected-node');
                if (stored) {
                    try {
                        const parsed = JSON.parse(stored);
                        this.selectedNodeKey = parsed;
                        return { ...parsed };
                    } catch (error) {
                        console.error('Error parsing stored selected node:', error);
                    }
                }
            }
            
            return { ...this.selectedNodeKey };
        },

        // Restore selected library and label IDs from persisted node selection
        restoreSelectedStateFromPersistedNode() {
            const selectedNodeKey = this.getSelectedNodeKey();
            const selectedNodeId = Object.keys(selectedNodeKey)[0];
            
            if (selectedNodeId && this.treeNodes.length > 0) {
                // Find the node in the tree to determine if it's a library or label
                const findNodeInTree = (nodes: any[], nodeId: string): any => {
                    for (const node of nodes) {
                        if (node.id === nodeId) {
                            return node;
                        }
                        if (node.children) {
                            const found = findNodeInTree(node.children, nodeId);
                            if (found) return found;
                        }
                    }
                    return null;
                };
                
                const selectedNode = findNodeInTree(this.treeNodes, selectedNodeId);
                
                if (selectedNode) {
                    if (selectedNode.isLibrary) {
                        // If it's a library, set library ID and clear label ID
                        this.selectedLibraryId = selectedNode.id;
                        this.selectedLabelId = null;
                    } else {
                        // If it's a label, set label ID and find its parent library
                        this.selectedLabelId = selectedNode.id;
                        
                        // Find the parent library for this label
                        const findParentLibrary = (nodes: any[], labelId: string): string | null => {
                            for (const node of nodes) {
                                if (node.isLibrary && node.children) {
                                    const hasLabel = (children: any[]): boolean => {
                                        return children.some(child => {
                                            if (child.id === labelId) return true;
                                            if (child.children) return hasLabel(child.children);
                                            return false;
                                        });
                                    };
                                    
                                    if (hasLabel(node.children)) {
                                        return node.id;
                                    }
                                }
                            }
                            return null;
                        };
                        
                        const parentLibraryId = findParentLibrary(this.treeNodes, selectedNode.id);
                        if (parentLibraryId) {
                            this.selectedLibraryId = parentLibraryId;
                        }
                    }
                    
                    return {
                        libraryId: this.selectedLibraryId,
                        labelId: this.selectedLabelId,
                        isLibrary: selectedNode.isLibrary
                    };
                }
            }
            
            return null;
        },

        // Save expanded state of tree nodes
        saveExpandedState(expandedKeys: Record<string, boolean>) {
            this.expandedKeys = { ...expandedKeys };
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('knowledge-expanded-state', JSON.stringify(expandedKeys));
            }
        },

        // Get expanded state of tree nodes
        getExpandedState(): Record<string, boolean> {
            // If we have state in memory, use it
            if (Object.keys(this.expandedKeys).length > 0) {
                return { ...this.expandedKeys };
            }
            
            // Otherwise, try to load from localStorage
            if (typeof localStorage !== 'undefined') {
                const stored = localStorage.getItem('knowledge-expanded-state');
                if (stored) {
                    try {
                        const parsed = JSON.parse(stored);
                        this.expandedKeys = parsed;
                        return { ...parsed };
                    } catch (error) {
                        console.error('Error parsing stored expanded state:', error);
                    }
                }
            }
            
            return { ...this.expandedKeys };
        },

        // Clear expanded state
        clearExpandedState() {
            this.expandedKeys = {};
            // Clear from localStorage
            if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('knowledge-expanded-state');
            }
        },

        // Article view state management actions
        setCurrentSelectedRevision(revision: any) {
            this.currentSelectedRevision = revision;
        },

        setCurrentArticleRevisions(revisions: ArticleRevision[]) {
            this.currentArticleRevisions = revisions;
        },

        setLoadingRevisions(loading: boolean) {
            this.loadingRevisions = loading;
        },

        async fetchRelatedArticles(revisionId: string, updated?: string) {
            this.loadingRelatedArticles = true;
            try {
                const knowledgeAPI = useKnowledgeAPI();
                const result = await knowledgeAPI.fetchRelatedArticles(revisionId, updated);
                this.relatedArticles = result.items;
                return result.items;
            } catch (error) {
                console.error('Error loading related articles:', error);
                this.relatedArticles = [];
                return [];
            } finally {
                this.loadingRelatedArticles = false;
            }
        },

        setRelatedArticles(articles: RelatedArticle[]) {
            this.relatedArticles = articles;
        },

        // UI state management actions
        setPanelMinimized(minimized: boolean) {
            this.isPanelMinimized = minimized;
            // Persist to localStorage
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('knowledge-panel-minimized', JSON.stringify(minimized));
            }
        },

        getPanelMinimized(): boolean {
            // If we have state in memory, use it
            if (this.isPanelMinimized !== undefined) {
                return this.isPanelMinimized;
            }
            
            // Otherwise, try to load from localStorage
            if (typeof localStorage !== 'undefined') {
                const stored = localStorage.getItem('knowledge-panel-minimized');
                if (stored) {
                    try {
                        const parsed = JSON.parse(stored);
                        this.isPanelMinimized = parsed;
                        return parsed;
                    } catch (error) {
                        console.error('Error parsing stored panel state:', error);
                    }
                }
            }
            
            // Default to false if window is wide enough, true if narrow
            const defaultMinimized = typeof window !== 'undefined' && window.innerWidth <= 1000;
            this.isPanelMinimized = defaultMinimized;
            return defaultMinimized;
        },

        setEditingState(editing: boolean) {
            this.isEditing = editing;
        },

        // Article library CSS cache management
        async fetchArticleLibraryCss(articleId: string) {
            // Check if we have it cached
            if (this.articleLibraryCssCache[articleId]) {
                return this.articleLibraryCssCache[articleId];
            }

            try {
                const knowledgeAPI = useKnowledgeAPI();
                const response = await knowledgeAPI.fetchKnowledgeListingArticle({
                    filter: [{ property: 'id', value: articleId }]
                });

                if (response.items && response.items.length > 0) {
                    const css = (response.items[0] as any).library_css || '';
                    this.articleLibraryCssCache[articleId] = css;
                    return css;
                }
            } catch (error) {
                console.error('Error fetching article library CSS:', error);
            }

            return '';
        },

        getArticleLibraryCss(articleId: string): string {
            return this.articleLibraryCssCache[articleId] || '';
        },

        // Clear article view state when leaving
        clearArticleViewState() {
            this.currentSelectedRevision = null;
            this.currentArticleRevisions = [];
            this.loadingRevisions = false;
            this.relatedArticles = [];
            this.loadingRelatedArticles = false;
            this.isEditing = false;
            // Don't clear panel minimized state as it should persist
        },

        // Clear tree data cache to force refresh
        clearTreeCache() {
            this.treeDataCache = null;
            this.treeDataCacheTimestamp = null;
        },

        // Reset all org-specific state on logout so the next session starts fresh
        resetSessionState() {
            // Tree & library data
            this.treeNodes = [];
            this.libraries = [];
            this.selectedLibrary = null;
            this.labelTree = [];
            this.labelMap = {};
            this.treeDataCache = null;
            this.treeDataCacheTimestamp = null;

            // Selection state
            this.selectedLibraryId = null;
            this.selectedLabelId = null;
            this.selectedNodeKey = {};
            this.expandedKeys = {};

            // List / content state
            this.items = [];
            this.currentList = [];
            this.allFilteredResults = [];
            this.totalCount = 0;
            this.currentNode = null;
            this.currentRootId = null;
            this.searchQuery = '';

            // Filters
            this.statusFilter = null;
            this.accessFilter = null;
            this.productsFilter = [];
            this.tagsFilter = [];

            // Article view state
            this.currentArticle = null;
            this.currentSelectedRevision = null;
            this.currentArticleRevisions = [];
            this.relatedArticles = [];
            this.articleLibraryCssCache = {};

            // Clear all knowledge-related localStorage keys
            if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('knowledge-library-filter');
                localStorage.removeItem('knowledge-label-filter');
                localStorage.removeItem('knowledge-selected-node');
                localStorage.removeItem('knowledge-status-filter');
                localStorage.removeItem('knowledge-access-filter');
                localStorage.removeItem('knowledge-products-filter');
                localStorage.removeItem('knowledge-tags-filter');
                localStorage.removeItem('knowledge-search-query');
                localStorage.removeItem('knowledge-expanded-state');
            }
        },

        // Refresh Shared with Me library visibility based on article count
        async refreshSharedWithMeLibrary() {
            const SHARED_WITH_ME_ID = '00000001-0001-0001-0001-000000000001';
            
            try {
                const knowledgeAPI = useKnowledgeAPI();
                const sharedWithMeCount = await knowledgeAPI.checkSharedWithMeArticleCount();
                
                // Check if Shared with Me library is currently in the tree
                const sharedWithMeIndex = this.treeNodes.findIndex(node => node.id === SHARED_WITH_ME_ID);
                const hasSharedWithMe = sharedWithMeIndex !== -1;
                
                if (sharedWithMeCount > 0 && !hasSharedWithMe) {
                    // Add Shared with Me library to the tree
                    const sharedWithMeNode = await knowledgeAPI.fetchSharedWithMeLibraryNode();
                    if (sharedWithMeNode) {
                        // Don't set article_cnt to keep it consistent with other libraries
                        this.treeNodes = [...this.treeNodes, sharedWithMeNode];
                        
                        // Update cache if it exists
                        if (this.treeDataCache) {
                            this.treeDataCache = [...this.treeDataCache, sharedWithMeNode];
                        }
                    }
                } else if (sharedWithMeCount === 0 && hasSharedWithMe) {
                    // Remove Shared with Me library from the tree
                    this.treeNodes = this.treeNodes.filter(node => node.id !== SHARED_WITH_ME_ID);
                    
                    // Update cache if it exists
                    if (this.treeDataCache) {
                        this.treeDataCache = this.treeDataCache.filter(node => node.id !== SHARED_WITH_ME_ID);
                    }
                }
                // Note: No need to update article count when library already exists and has articles
            } catch (error) {
                console.error('Error refreshing Shared with Me library:', error);
            }
        },
    },
});
